import { Button, IconButton, IconButtonProps, Tooltip } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'

import Iconify from 'src/components/Iconify'
import { icons, toneGlyph } from 'src/design-system'
import { motion, typography } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// One press, one clipboard, one component.
//
// This was the end adornment of `CopyableInput` and nowhere else, so the only
// values in the console you could copy without selecting them by hand were the
// two long URLs that component happened to be drawn for. A Firebase uid, 28
// characters of opaque string and the one value on a driver's record that
// exists solely to be pasted somewhere else, had to be dragged across a box
// that clipped it.
//
// Lifted out verbatim rather than rewritten, so there is one copy control
// rather than two that drift. `CopyableInput` now draws this one too.
//
// **The control says it worked, and the snackbar is not enough.** A press put
// a message in the corner of the screen and left the button exactly as it
// was, which is the shape that makes an operator press it twice: the answer
// to "did that land" was three hundred pixels away from the thing that was
// pressed, and gone by the time they looked back. So the mark itself becomes
// the success tone's glyph for the confirm dwell, and the control's name and
// its hint say `Copied` for the same window. The snackbar stays (it is what a
// reader who is not watching this corner of the screen gets), but it is no
// longer the only report.
//
// The glyph is `toneGlyph('success')` and not a typed check: success has one
// mark across the console, it is bound in the token source, and a control
// that spells it here is a control that keeps drawing the old one the day it
// moves.
//
// **The dwell is a state that ends, and its timer is cleared on unmount.** The
// resting name is what a fresh render shows, so a row redrawn while a button
// is still green comes back saying `Copy to clipboard`; and a button pressed
// and then navigated away from does not set state on a component that is gone.
// A second press inside the window restarts the dwell rather than inheriting
// the tail of the first one, which is why the handle is a ref and not an
// effect keyed on the flag.
//
// **`showValue` draws the value too, and the value is part of the control.**
// Asked for on 2026-10-21, for the rentals lists: "Add easily copiable rental
// number in its own column after Actions with copy icon (but also copies when
// just clicked on the text area)". So the number and the copy mark are one
// ghost button, the value as its words and the mark after them: a press
// anywhere on it copies, and the mark, the name and the snackbar report it
// exactly as the icon-only control does. One button rather than a text with
// a click handler beside a button, so it is one target, one focus stop and
// one name (`Copy rental number 45013`), and Enter or Space copies it. A
// ghost on a table cell's edge puts its words on the column's edge, which
// the table's theme already does for every ghost there. The value keeps the
// body's regular weight rather than a button's semibold, so a column of them
// reads as figures and not as a column of commands; its numerals are tabular
// because every button's are, which the theme's button states once.
// ----------------------------------------------------------------------

/** What the control calls itself while it is reporting, not asking. */
export const COPIED = 'Copied'

export interface CopyButtonProps extends Omit<IconButtonProps, 'onClick'> {
  /** The exact text put on the clipboard. */
  value: string
  /** What the tooltip and the accessible name call it. */
  label?: string
  /**
   * Draw the value as the control's words, with the copy mark after it, so a
   * press on the text copies too. Without it the control is the mark alone.
   */
  showValue?: boolean
}

export default function CopyButton({
  value,
  label = 'Copy to clipboard',
  size = 'small',
  showValue = false,
  sx,
  edge,
  color,
  ...rest
}: CopyButtonProps) {
  const { enqueueSnackbar } = useSnackbar()
  const [copied, setCopied] = useState(false)
  const dwell = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(dwell.current), [])

  const confirm = () => {
    enqueueSnackbar('Copied!', { variant: 'success' })
    setCopied(true)
    clearTimeout(dwell.current)
    dwell.current = setTimeout(() => setCopied(false), motion.dwell.confirm)
  }

  // One name for the control, its hint and its state. They were never going
  // to be allowed to disagree, and writing them once is how that stays true.
  const name = copied ? COPIED : label

  const glyph = (
    <Iconify icon={copied ? toneGlyph('success') : icons.copy} size="chip" />
  )

  if (showValue) {
    return (
      <CopyToClipboard text={value} onCopy={confirm}>
        <Tooltip title={name}>
          {/* The value is the words, so the name says what pressing does and
              to what: `Copy rental number 45013`. While it reports, the name
              is the report. The IconButton's own `edge` and `color` are
              not a Button's, and stay off it. */}
          <Button
            variant="text"
            color="inherit"
            size={size}
            aria-label={copied ? COPIED : `${label} ${value}`}
            endIcon={glyph}
            sx={[
              {
                'fontWeight': typography.weights.regular,
                '& .MuiButton-endIcon': {
                  color: copied ? 'success.main' : 'text.secondary',
                },
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...rest}
          >
            {value}
          </Button>
        </Tooltip>
      </CopyToClipboard>
    )
  }

  return (
    <CopyToClipboard text={value} onCopy={confirm}>
      <Tooltip title={name}>
        {/* The name is on the control, not on the glyph: the glyph is
            `aria-hidden` like every other mark in the console. */}
        <IconButton
          size={size}
          aria-label={name}
          edge={edge}
          color={color}
          sx={[
            copied ? { color: 'success.main' } : false,
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...rest}
        >
          {glyph}
        </IconButton>
      </Tooltip>
    </CopyToClipboard>
  )
}
